import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import Cookies from 'js-cookie';
import { getAuthUrl } from '@podus/utils';
import { podusApi } from '@/lib/podusApi';

const TOKEN_COOKIE = 'p-ai-accessToken';

interface AuthContextValue {
    isLoggedIn: boolean;
    token: string | null;
    openLogin: () => void;
    openSignup: () => void;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Pull `?accessToken=…` out of the URL (set by auth service redirects) and
 * persist it as a cookie. Done synchronously so the first render of
 * `<ProtectedRoute>` already sees the token instead of redirecting back to
 * auth before the effect could run.
 */
function extractTokenFromUrl(): string | null {
    if (typeof window === 'undefined') return null;
    const url = new URL(window.location.href);
    const urlToken = url.searchParams.get('accessToken');
    if (!urlToken) return null;

    Cookies.set(TOKEN_COOKIE, urlToken, { expires: 365 * 100 });
    url.searchParams.delete('accessToken');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    return urlToken;
}

export function AuthProvider({ children }: { children: ReactNode }) {
    const [token, setToken] = useState<string | null>(
        () => extractTokenFromUrl() ?? Cookies.get(TOKEN_COOKIE) ?? null,
    );
    const authBase = getAuthUrl();

    // Periodically check cookie (for cross-tab sync)
    useEffect(() => {
        const check = () => {
            const current = Cookies.get(TOKEN_COOKIE) ?? null;
            setToken(current);
        };
        const interval = setInterval(check, 2000);
        window.addEventListener('storage', check);
        return () => {
            clearInterval(interval);
            window.removeEventListener('storage', check);
        };
    }, []);

    const openLogin = useCallback(() => {
        const redirect = encodeURIComponent(window.location.href);
        window.location.href = `${authBase}/sign-in?redirect=${redirect}`;
    }, [authBase]);

    const openSignup = useCallback(() => {
        const redirect = encodeURIComponent(window.location.href);
        window.location.href = `${authBase}/sign-up?redirect=${redirect}`;
    }, [authBase]);

    const logout = useCallback(async () => {
        // Tell NEW_PODUS_API to bump tokensValidAfter so every JWT ever issued
        // to this user is rejected going forward. Swallow errors so a failed
        // server call never blocks local sign-out.
        try {
            await podusApi.post('/acquisition/logout');
        } catch (e) {
            console.warn('logout API call failed', e);
        }
        Cookies.remove(TOKEN_COOKIE);
        setToken(null);
        // Bounce through auth's /sign-out so it clears its own cookie too —
        // without this, /sign-in auto-redirects back with the same token.
        const redirectTarget = `${window.location.origin}/`;
        window.location.href = `${authBase}/sign-out?redirect=${encodeURIComponent(redirectTarget)}`;
    }, [authBase]);

    return (
        <AuthContext.Provider value={{ isLoggedIn: !!token, token, openLogin, openSignup, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
